import { SMTImage, SMTEntry as SMTComponentEntry } from '@equip.health/ui';

import {
  createSMTEntryKebabMenuOptions,
  SMTEntryKebabMenuHandlers,
} from '~/components/maud/patients/profile/smt/utils';
import util from '~/lib/util/util';

export interface SMTImageData {
  url: string;
  thumbnailUrl: string;
  fileName: string;
}

interface TransformSmtEntryParams {
  entry: SMTEntry;
  handleImageClick: (url: string, fileName: string) => void;
  handlers?: SMTEntryKebabMenuHandlers;
  onMenuOpen?: (entryId: string) => void;
}

/**
 * Formats ISO datetime string to 12-hour format with AM/PM (e.g., "2:30 PM")
 * Expects format: "YYYY-MM-DDTHH:mm:ss" (no Z suffix)
 */
export const formatTime = (timeString: string): string => {
  if (!timeString) {
    return '';
  }

  // Extract HH:mm from ISO datetime string (format: "YYYY-MM-DDTHH:mm:ss")
  const timeRegex = /T(\d{2}):(\d{2})/;
  const timeMatch = timeRegex.exec(timeString);
  if (!timeMatch) {
    return '';
  }

  const hours = Number.parseInt(timeMatch[1], 10);
  const minutes = timeMatch[2];
  if (Number.isNaN(hours)) {
    return '';
  }
  return `${hours % 12 || 12}:${minutes} ${hours >= 12 ? 'PM' : 'AM'}`;
};

/**
 * Transforms API SMTImage to image data object
 */
export const transformImageData = (
  image: SMTImage,
): SMTImageData | undefined => {
  if (!image.url || !image.thumbnailUrl) {
    return undefined;
  }

  return {
    fileName: image.fileName || '',
    thumbnailUrl: image.thumbnailUrl,
    url: image.url,
  };
};

/**
 * Transforms API SMTComments to the format expected by the UI component
 */
export const transformComments = (
  comments: SMTComments[] | null,
): Array<{
  comment: string;
  commenterAvatarProps: { name: string; src?: string };
  id: string;
}> => {
  if (!comments || comments.length === 0) {
    return [];
  }

  return comments.map((comment) => {
    const { createdByUserFirstName, createdByUserLastName } = comment;
    const name = `${createdByUserFirstName} ${createdByUserLastName}`.trim();
    return {
      comment: comment.comment ?? '',
      commenterAvatarProps: {
        name,
        src: comment.createdByUserAvatarDetails?.thumbnailUrl,
      },
      id: comment.id,
    };
  });
};

/**
 * Transforms API SMTEntry to the format expected by the UI component
 * @param params - Object containing entry, handlers, and optional configuration
 * @param params.entry - The API SMTEntry to transform
 * @param params.handleImageClick - Handler for image click events
 * @param params.handlers - Optional handlers for kebab menu actions
 * @param params.onMenuOpen - Optional callback when kebab menu is opened
 * @returns Transformed SMTComponentEntry with optional kebab menu options
 */
export const transformSmtEntry = ({
  entry,
  handleImageClick,
  handlers,
  onMenuOpen,
}: TransformSmtEntryParams): SMTComponentEntry => {
  const displayFirstName =
    entry.createdByUserDisplayFirstName || entry.createdByUserFirstName || '';
  const displayLastName =
    entry.createdByUserDisplayLastName || entry.createdByUserLastName || '';
  const createdByUserName = `${displayFirstName} ${displayLastName}`.trim();

  const firstImage =
    entry.images && entry.images.length > 0 ? entry.images[0] : null;
  const imageData = firstImage ? transformImageData(firstImage) : undefined;

  const avatarDetails = {
    name: createdByUserName,
    src: entry.createdByUserAvatarDetails?.thumbnailUrl || undefined,
  };

  return {
    entry: {
      addedByUserAvatarProps: {
        ...avatarDetails,
        userType: entry.createdByUserType,
      },
      calories: entry.calories ?? undefined,
      caloriesLabeledByAvatarProps: {},
      comment: entry.comment,
      description: entry.description,
      id: entry.id,
      image: imageData ? (
        <SMTImage
          alt={imageData.fileName}
          height="120px"
          onButtonClick={() =>
            handleImageClick(imageData.url, imageData.fileName)
          }
          src={imageData.thumbnailUrl}
          width="120px"
        />
      ) : undefined,
      isNewFood: entry.isNewFood,
      kebabMenuOptions: handlers
        ? createSMTEntryKebabMenuOptions(
            entry.id,
            {
              ...handlers,
              onEdit: (entryId: string, entryData?: SMTEntry) =>
                handlers.onEdit(entryId, entryData),
            },
            entry,
          )
        : [],
      onMenuOpen: onMenuOpen
        ? (entryId: string) => onMenuOpen(entryId)
        : undefined,
      providerComments: transformComments(entry.comments ?? null),
      time: formatTime(entry.occurredAt ?? ''),
      title: util.capitalize(entry.mealType?.toLowerCase() ?? ''),
    },
    entryType: entry.entryType,
    id: entry.id,
  };
};
